/**
 * Legal Documents API Client with React Query Hooks
 *
 * Provides type-safe API client for Legal Documents management using:
 * - Zod for runtime validation
 * - React Query for data fetching and caching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { authApiGet, authApiPut } from 'services/authApi';

// ============================================================================
// Zod Schemas
// ============================================================================

const LegalDocumentSchema = z.object({
  id: z.number(),
  documentType: z.string(),
  version: z.string(),
  content: z.string(),
  effectiveDate: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
});

export type LegalDocument = z.infer<typeof LegalDocumentSchema>;

export interface UpdateLegalDocumentRequest {
  content: string;
  version: string;
  effectiveDate: string;
}

// ============================================================================
// Transformation Functions
// ============================================================================

function transformSnakeToCamel<T>(obj: Record<string, unknown>): T {
  const transformed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    transformed[camelKey] = value;
  }
  return transformed as T;
}

function transformCamelToSnake(obj: Record<string, unknown>): Record<string, unknown> {
  const transformed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    transformed[snakeKey] = value;
  }
  return transformed;
}

// ============================================================================
// Fetcher Functions
// ============================================================================

const fetchLegalDocuments = async (): Promise<LegalDocument[]> => {
  const data = await authApiGet<unknown[]>('/api/admin/legal');
  return z
    .array(LegalDocumentSchema)
    .parse(data.map((item) => transformSnakeToCamel(item as Record<string, unknown>)));
};

const updateLegalDocument = async (
  documentType: string,
  request: UpdateLegalDocumentRequest
): Promise<LegalDocument> => {
  const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
  const data = await authApiPut<unknown>(`/api/admin/legal/${documentType}`, payload);
  return LegalDocumentSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
};

// ============================================================================
// React Query Hooks
// ============================================================================

export const useLegalDocuments = () => {
  return useQuery({
    queryKey: ['admin-legal-documents'],
    queryFn: fetchLegalDocuments,
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdateLegalDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentType,
      request,
    }: {
      documentType: string;
      request: UpdateLegalDocumentRequest;
    }) => updateLegalDocument(documentType, request),
    onSuccess: (updatedDoc) => {
      queryClient.setQueryData<LegalDocument[]>(['admin-legal-documents'], (oldData) => {
        if (!oldData) return [updatedDoc];
        return oldData.map((doc) =>
          doc.documentType === updatedDoc.documentType ? updatedDoc : doc
        );
      });
      queryClient.invalidateQueries({ queryKey: ['admin-legal-documents'] });
    },
  });
};
